import { useMemo, useState, type FormEvent, type ReactNode } from 'react'

import { useSesion } from '@/modules/auth/useSesion'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Boton } from '@/shared/ui/Boton/Boton'
import { Cargando } from '@/shared/ui/Estados/Cargando'
import { SinDatos } from '@/shared/ui/Estados/SinDatos'
import { Modal } from '@/shared/ui/Modal/Modal'
import { Tabla, type ColumnaTabla } from '@/shared/ui/Tabla/Tabla'
import { Tarjeta } from '@/shared/ui/Tarjeta/Tarjeta'
import { normalizar } from '@/shared/utilidades/formato'

import { FormularioMaestro } from './FormularioMaestro'
import { useMaestro } from './useMaestro'
import type { CampoMaestro, RegistroMaestro, TablaMaestro } from './tipos'
import estilos from './PaginaMaestro.module.css'

type PropsPaginaMaestro<T extends RegistroMaestro> = {
  titulo: string
  subtitulo: string
  tabla: TablaMaestro
  ordenarPor: string
  /** Qué columnas traer, si hace falta algo más que las de la tabla. */
  seleccion?: string
  /** Cómo se llama un registro en singular: "cliente", "zona". */
  singular: string
  columnas: ColumnaTabla<T>[]
  campos: CampoMaestro[]
  /** Texto de cada registro sobre el que busca el buscador. */
  textoDeBusqueda: (registro: T) => string
  /** Valores por defecto al crear uno nuevo. */
  valoresNuevos?: Record<string, unknown>
  /**
   * Un mando propio del maestro, a la izquierda del buscador.
   *
   * Existe para lo que solo tiene sentido en UN maestro y no en los seis
   * —imprimir las etiquetas de las herramientas—. Va aquí y no en el menú
   * porque es algo que se hace dos veces al año.
   */
  mandoExtra?: ReactNode
  /** Nota fija arriba de la tabla: qué falta, o de dónde salen los datos. */
  nota?: ReactNode
}

/**
 * Pantalla estándar de un maestro: buscar, crear, editar y desactivar.
 *
 * Las cinco entidades maestras la comparten. Cada una aporta sus columnas y
 * sus campos; el comportamiento —permisos, búsqueda, guardado, errores— vive
 * aquí una sola vez.
 */
export function PaginaMaestro<T extends RegistroMaestro>({
  titulo,
  subtitulo,
  tabla,
  ordenarPor,
  seleccion,
  mandoExtra,
  singular,
  columnas,
  campos,
  textoDeBusqueda,
  nota,
  valoresNuevos = {},
}: PropsPaginaMaestro<T>) {
  const { puede } = useSesion()

  const [busqueda, setBusqueda] = useState('')
  const [verInactivos, setVerInactivos] = useState(false)
  const [enEdicion, setEnEdicion] = useState<T | null>(null)
  const [creando, setCreando] = useState(false)
  const [valores, setValores] = useState<Record<string, unknown>>({})
  const [errorGuardado, setErrorGuardado] = useState<string | null>(null)

  const { registros, cargando, error, crear, actualizar, cambiarActivo } = useMaestro<T>({
    tabla,
    ordenarPor,
    seleccion,
    incluirInactivos: verInactivos,
  })

  const puedeCrear = puede('maestros', 'crear')
  const puedeEditar = puede('maestros', 'editar')
  const formularioAbierto = creando || enEdicion !== null

  const filtrados = useMemo(() => {
    const termino = normalizar(busqueda)
    if (!termino) return registros

    return registros.filter((registro) => normalizar(textoDeBusqueda(registro)).includes(termino))
  }, [registros, busqueda, textoDeBusqueda])

  function abrirNuevo() {
    setValores({ ...valoresNuevos })
    setErrorGuardado(null)
    setCreando(true)
  }

  function abrirEdicion(registro: T) {
    if (!puedeEditar) return

    // Se copian solo los campos declarados: mandar de vuelta creado_en o
    // columnas calculadas haría fallar el UPDATE.
    const copia: Record<string, unknown> = {}
    for (const campo of campos) {
      copia[campo.nombre] = registro[campo.nombre] ?? ''
    }

    setValores(copia)
    setErrorGuardado(null)
    setEnEdicion(registro)
  }

  function cerrarFormulario() {
    setCreando(false)
    setEnEdicion(null)
    setValores({})
    setErrorGuardado(null)
  }

  async function guardar(evento: FormEvent) {
    evento.preventDefault()
    setErrorGuardado(null)

    try {
      if (enEdicion) {
        await actualizar.mutateAsync({ id: enEdicion.id, valores })
      } else {
        await crear.mutateAsync(valores)
      }

      cerrarFormulario()
    } catch (fallo) {
      setErrorGuardado(fallo instanceof Error ? fallo.message : 'No fue posible guardar.')
    }
  }

  const guardando = crear.isPending || actualizar.isPending

  // La columna de estado solo aparece cuando se están viendo los desactivados:
  // en el uso diario todos están activos y la columna sería ruido.
  const columnasConEstado: ColumnaTabla<T>[] = verInactivos
    ? [
        ...columnas,
        {
          encabezado: 'Estado',
          ancho: '110px',
          celda: (registro) => (registro.activo ? 'Activo' : 'Desactivado'),
        },
      ]
    : columnas

  return (
    <>
      <Tarjeta
        titulo={titulo}
        subtitulo={subtitulo}
        sinRelleno
        pie={`${filtrados.length} de ${registros.length} registros`}
        acciones={
          <div className={estilos.herramientas}>
            {mandoExtra}

            <input
              className={estilos.buscador}
              type="search"
              placeholder={`Buscar ${singular}…`}
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              aria-label={`Buscar ${singular}`}
            />

            <label className={estilos.filtroInactivos}>
              <input
                type="checkbox"
                checked={verInactivos}
                onChange={(e) => setVerInactivos(e.target.checked)}
              />
              Ver desactivados
            </label>

            {puedeCrear && (
              <Boton variante="principal" onClick={abrirNuevo}>
                Nuevo
              </Boton>
            )}
          </div>
        }
      >
        {nota && <div style={{ padding: 'var(--e-4) var(--e-5) 0' }}>{nota}</div>}

        {cargando ? (
          <Cargando mensaje={`Cargando ${titulo.toLowerCase()}…`} />
        ) : error ? (
          <div style={{ padding: 'var(--e-5)' }}>
            <Aviso tipo="error" titulo="No se pudieron cargar los datos">
              {error}
            </Aviso>
          </div>
        ) : (
          <Tabla
            columnas={columnasConEstado}
            filas={filtrados}
            claveFila={(registro) => registro.id}
            onAbrirFila={puedeEditar ? abrirEdicion : undefined}
            vacio={
              <SinDatos
                titulo={busqueda ? 'Ningún resultado' : `Todavía no hay ${titulo.toLowerCase()}`}
                mensaje={
                  busqueda
                    ? 'Pruebe con otro término de búsqueda.'
                    : puedeCrear
                      ? `Cree el primer ${singular} con el botón «Nuevo».`
                      : undefined
                }
              />
            }
          />
        )}
      </Tarjeta>

      <Modal
        abierto={formularioAbierto}
        titulo={enEdicion ? `Editar ${singular}` : `Nuevo ${singular}`}
        subtitulo={
          enEdicion
            ? 'Los cambios afectan los registros nuevos; el historial conserva lo que decía antes.'
            : undefined
        }
        onCerrar={cerrarFormulario}
        pie={
          <>
            {enEdicion && puedeEditar && (
              <Boton
                variante={enEdicion.activo ? 'peligro' : 'secundario'}
                onClick={async () => {
                  await cambiarActivo.mutateAsync({
                    id: enEdicion.id,
                    activo: !enEdicion.activo,
                  })
                  cerrarFormulario()
                }}
                trabajando={cambiarActivo.isPending}
              >
                {enEdicion.activo ? 'Desactivar' : 'Reactivar'}
              </Boton>
            )}

            <Boton onClick={cerrarFormulario}>Cancelar</Boton>

            <Boton
              variante="principal"
              onClick={(e) => void guardar(e)}
              trabajando={guardando}
            >
              Guardar
            </Boton>
          </>
        }
      >
        <form onSubmit={(e) => void guardar(e)}>
          {errorGuardado && (
            <div className={estilos.errorGuardado}>
              <Aviso tipo="error">{errorGuardado}</Aviso>
            </div>
          )}

          <FormularioMaestro
            campos={campos}
            valores={valores}
            deshabilitado={guardando}
            onCambiar={(nombre, valor) =>
              setValores((previos) => ({ ...previos, [nombre]: valor }))
            }
          />

          {/* Botón oculto: hace que Enter dentro de un campo envíe el
              formulario, como espera cualquiera que digite rápido. */}
          <button type="submit" className="solo-lectores" tabIndex={-1}>
            Guardar
          </button>
        </form>
      </Modal>
    </>
  )
}
